import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';

export interface PaginationProps {
  /** Current active page (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Custom className */
  className?: string;
}

export const handlePreviousClick = (
  currentPage: number,
  onPageChange: (page: number) => void
): void => {
  if (currentPage > 1) {
    onPageChange(currentPage - 1);
  }
};

export const handleNextClick = (
  currentPage: number,
  totalPages: number,
  onPageChange: (page: number) => void
): void => {
  if (currentPage < totalPages) {
    onPageChange(currentPage + 1);
  }
};

/**
 * Pagination component for navigating pages
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   onPageChange={setCurrentPage}
 * />
 * ```
 */
export const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
}) => {
  const { t } = useTranslation('common');

  const handlePrevious = () => {
    handlePreviousClick(currentPage, onPageChange);
  };

  const handleNext = () => {
    handleNextClick(currentPage, totalPages, onPageChange);
  };

  const handlePageClick = (page: number) => {
    if (page !== currentPage) {
      onPageChange(page);
    }
  };

  const pages = Array.from({ length: totalPages }, (_, i) => i + 1);

  return (
    <nav
      className={cn('flex items-center justify-center gap-2', className)}
      aria-label={t('pagination.aria_label')}
    >
      {/* Previous button */}
      <button
        type="button"
        onClick={handlePrevious}
        disabled={currentPage === 1}
        className={cn(
          'px-3 py-2 rounded border text-sm font-medium transition-colors',
          currentPage === 1
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
        )}
        aria-label={t('pagination.go_to_previous')}
      >
        {t('pagination.previous')}
      </button>

      {/* Page numbers */}
      {pages.map((page) => (
        <button
          key={page}
          type="button"
          onClick={() => handlePageClick(page)}
          className={cn(
            'px-3 py-2 rounded text-sm font-medium transition-colors',
            page === currentPage
              ? 'bg-blue-600 text-white'
              : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-300'
          )}
          aria-label={t('pagination.go_to_page', { page })}
          aria-current={page === currentPage ? 'page' : undefined}
        >
          {page}
        </button>
      ))}

      {/* Next button */}
      <button
        type="button"
        onClick={handleNext}
        disabled={currentPage === totalPages}
        className={cn(
          'px-3 py-2 rounded border text-sm font-medium transition-colors',
          currentPage === totalPages
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300'
        )}
        aria-label={t('pagination.go_to_next')}
      >
        {t('pagination.next')}
      </button>
    </nav>
  );
};

Pagination.displayName = 'Pagination';
